import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import {
  useBusinessBySlug,
  useCategoryProducts,
  useToggleCategory,
  useBusinessExtras,
  useUpdateProduct,
  useDeleteExtra,
} from "../../../hooks/useProducts";
import { useBusinessProducts } from "../../../hooks/useBusinessCatalog";
import { Icon, Breadcrumb, Modal } from "../../ui";
import { DeleteConfirmationModal } from "../../ui/Modals";

import {
  CategoriesList,
  ProductsList,
  ExtrasList,
  ProductEditTab,
  CreateProductModal,
  CreateExtraModal,
  POSIntegrationBanner,
} from "../../ui/Products";

const PRODUCT_PAGE_SIZE = 10;

const flattenCategories = (categories = []) => {
  const flattened = [];

  const visit = (items = []) => {
    items.forEach((item) => {
      flattened.push(item);
      if (Array.isArray(item?.subcategories) && item.subcategories.length > 0) {
        visit(item.subcategories);
      }
    });
  };

  visit(categories);
  return flattened;
};

const resolveProductCategoryId = (product) => {
  const categoryId =
    product?.category_id ??
    product?.categoryId ??
    product?.category?.id ??
    product?.category?.category_id;

  return categoryId ?? null;
};

const resolveProductCategoryName = (product, categoryNameById) => {
  const directName =
    product?.category_name ??
    product?.categoryName ??
    product?.category?.name ??
    product?.category?.title;

  if (directName) {
    return directName;
  }

  const categoryId = resolveProductCategoryId(product);
  if (categoryId == null) {
    return "";
  }

  return categoryNameById[String(categoryId)] || "";
};

const ProductsBusinessPage = () => {
  const { t } = useTranslation(["products", "common"]);
  const { slug } = useParams();
  const toast = useToast();

  const { data: selectedBusiness, isLoading: isLoadingBusiness } =
    useBusinessBySlug(slug);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [productPage, setProductPage] = useState(1);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productSearchInput, setProductSearchInput] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isExtrasModalOpen, setIsExtrasModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("extras");
  const [isCreateProductOpen, setIsCreateProductOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [isCreateExtraOpen, setIsCreateExtraOpen] = useState(false);
  const [isDeleteExtraOpen, setIsDeleteExtraOpen] = useState(false);
  const [extraToDelete, setExtraToDelete] = useState(null);
  const [extraToEdit, setExtraToEdit] = useState(null);

  useEffect(() => {
    const timer = setTimeout(
      () => setProductSearchTerm(productSearchInput),
      300,
    );
    return () => clearTimeout(timer);
  }, [productSearchInput]);

  useEffect(() => {
    setProductPage(1);
  }, [productSearchTerm]);

  const categories = selectedBusiness?.categories || [];
  const flattenedCategories = useMemo(
    () => flattenCategories(categories),
    [categories],
  );
  const allCategoryIds = useMemo(
    () => flattenedCategories.map((category) => category.id),
    [flattenedCategories],
  );
  const categoryNameById = useMemo(() => {
    const namesById = {};
    flattenedCategories.forEach((category) => {
      namesById[String(category.id)] = category.name || "";
    });
    return namesById;
  }, [flattenedCategories]);

  useEffect(() => {
    if (categories.length > 0) {
      if (!selectedCategoryId) {
        setSelectedCategoryId(categories[0].id);
      } else {
        if (!allCategoryIds.includes(selectedCategoryId)) {
          setSelectedCategoryId(categories[0].id);
          setSelectedProduct(null);
          setProductPage(1);
        }
      }
    } else if (selectedCategoryId) {
      setSelectedCategoryId(null);
      setSelectedProduct(null);
    }
  }, [allCategoryIds, categories, selectedCategoryId]);

  const isGlobalSearch = productSearchTerm.trim().length > 0;

  const { data: productsData, isLoading: isLoadingProducts } =
    useCategoryProducts({
      businessId: selectedBusiness?.id,
      categoryId: selectedCategoryId,
      page: productPage,
      pageSize: PRODUCT_PAGE_SIZE,
      searchTerm: productSearchTerm,
    });
  const selectedCategoryProducts = productsData?.products || [];
  const productsPagination = productsData?.pagination || {};

  const { data: allProductsData, isLoading: isLoadingAllProducts } =
    useBusinessProducts(selectedBusiness?.id, {
      enabled: Boolean(selectedBusiness?.id) && isGlobalSearch,
      apiParams: {
        params:
          "id,name,description,price,images,enabled,category_id,category,extras,upselling,tax_id,tax",
      },
    });

  const normalizedAllProducts = useMemo(() => {
    const productsList = Array.isArray(allProductsData) ? allProductsData : [];

    return productsList.map((product) => {
      const categoryId = resolveProductCategoryId(product);
      return {
        ...product,
        category_id: categoryId,
        category_name: resolveProductCategoryName(product, categoryNameById),
      };
    });
  }, [allProductsData, categoryNameById]);

  const filteredAllProducts = useMemo(() => {
    const normalizedSearch = productSearchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return normalizedAllProducts;
    }

    return normalizedAllProducts.filter((product) => {
      const name = String(product?.name || "").toLowerCase();
      const description = String(product?.description || "").toLowerCase();
      return (
        name.includes(normalizedSearch) ||
        description.includes(normalizedSearch)
      );
    });
  }, [normalizedAllProducts, productSearchTerm]);

  const allProductsPagination = useMemo(() => {
    const totalItems = filteredAllProducts.length;
    return {
      total_items: totalItems,
      total_pages: Math.max(1, Math.ceil(totalItems / PRODUCT_PAGE_SIZE)),
    };
  }, [filteredAllProducts.length]);

  const paginatedAllProducts = useMemo(() => {
    const start = (productPage - 1) * PRODUCT_PAGE_SIZE;
    return filteredAllProducts.slice(start, start + PRODUCT_PAGE_SIZE);
  }, [filteredAllProducts, productPage]);

  useEffect(() => {
    if (!isGlobalSearch) {
      return;
    }

    if (productPage > allProductsPagination.total_pages) {
      setProductPage(allProductsPagination.total_pages);
    }
  }, [allProductsPagination.total_pages, isGlobalSearch, productPage]);

  const displayedProducts = isGlobalSearch
    ? paginatedAllProducts
    : selectedCategoryProducts;
  const displayedProductsPagination = isGlobalSearch
    ? allProductsPagination
    : productsPagination;
  const isLoadingCurrentProducts = isGlobalSearch
    ? isLoadingAllProducts
    : isLoadingProducts;

  const { data: extrasData, isLoading: isLoadingExtras } = useBusinessExtras({
    businessId: selectedBusiness?.id,
  });
  const extras = extrasData?.extras || [];

  const hasPosIntegration = useMemo(() => {
    return (
      selectedBusiness?.third_party_integrations?.some(
        (integration) => integration?.third_party_integration?.type === "pos",
      ) ?? false
    );
  }, [selectedBusiness?.third_party_integrations]);

  const toggleCategoryMutation = useToggleCategory();
  const toggleProductMutation = useUpdateProduct({
    optimisticUpdate: true,
    invalidateQueries: ["category-products", "business-products-v2"],
    optimisticUpdateFn: ({
      businessId,
      categoryId,
      productId,
      payload,
      queryClient,
    }) => {
      const { enabled } = payload;
      // Optimistic update: update enabled flag across all cached pages for this category
      const previousEntries = [];
      const allQueries = queryClient.getQueriesData({
        queryKey: ["category-products"],
      });
      allQueries.forEach(([key, data]) => {
        // key format: ['category-products', businessId, categoryId, page, pageSize, searchTerm]
        if (!Array.isArray(key)) return;
        const [, qBusinessId, qCategoryId] = key;
        if (
          qBusinessId === businessId &&
          qCategoryId === categoryId &&
          data?.products
        ) {
          previousEntries.push([key, data]);
          queryClient.setQueryData(key, {
            ...data,
            products: data.products.map((p) =>
              p.id === productId ? { ...p, enabled } : p,
            ),
          });
        }
      });

      const businessQueries = queryClient.getQueriesData({
        queryKey: ["business-products-v2", businessId],
      });
      businessQueries.forEach(([key, data]) => {
        if (!Array.isArray(key) || !Array.isArray(data)) return;

        previousEntries.push([key, data]);
        queryClient.setQueryData(
          key,
          data.map((product) =>
            product.id === productId ? { ...product, enabled } : product,
          ),
        );
      });

      return { previousEntries };
    },
  });
  const updateExtrasMutation = useUpdateProduct({
    invalidateQueries: ["business-extras", "category-products"],
    transformPayload: (payload, { businessId, categoryId, productId }) => {
      // Transform extras array to JSON string if present
      const { extras, business_id, category_id, product_id, ...rest } = payload;
      return {
        business_id: business_id || businessId,
        category_id: category_id || categoryId,
        product_id: product_id || productId,
        extras: extras ? JSON.stringify(extras) : undefined,
        ...rest,
      };
    },
  });
  const deleteExtraMutation = useDeleteExtra();

  const handleCategoryToggle = async (categoryId, enabled) => {
    try {
      await toggleCategoryMutation.mutateAsync({
        businessId: selectedBusiness.id,
        categoryId,
        enabled,
        slug,
      });
      handleApiSuccess(t("business.toasts.categoryUpdated"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("business.toasts.categoryUpdateFailedTitle"),
        defaultMessage: t("business.toasts.categoryUpdateFailedMessage"),
      });
    }
  };

  const handleProductToggle = async (productId, enabled) => {
    try {
      const productsSource = isGlobalSearch
        ? filteredAllProducts
        : selectedCategoryProducts;
      const product =
        productsSource.find((item) => item.id === productId) || selectedProduct;
      const categoryId =
        resolveProductCategoryId(product) ?? selectedCategoryId;

      if (!categoryId) {
        throw new Error("missing_category_id");
      }

      await toggleProductMutation.mutateAsync({
        businessId: selectedBusiness.id,
        categoryId,
        productId,
        enabled,
      });
      setSelectedProduct((previous) =>
        previous?.id === productId ? { ...previous, enabled } : previous,
      );
      handleApiSuccess(t("business.toasts.productUpdated"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("business.toasts.productUpdateFailedTitle"),
        defaultMessage: t("business.toasts.productUpdateFailedMessage"),
      });
    }
  };

  const handleExtraToggle = async (extra, currentExtras) => {
    try {
      const currentExtrasArray = currentExtras ?? [];
      const isEnabled = currentExtrasArray
        .map((e) => e?.id)
        .includes(extra?.id);
      const newExtras = isEnabled
        ? currentExtrasArray.filter((e) => e?.id !== extra?.id)
        : [...currentExtrasArray, extra];

      await updateExtrasMutation.mutateAsync({
        businessId: selectedBusiness.id,
        categoryId: selectedProduct?.category_id,
        productId: selectedProduct.id,
        business_id: selectedBusiness.id,
        category_id: selectedProduct?.category_id,
        product_id: selectedProduct.id,
        extras: newExtras.map((e) => e?.id),
      });

      setSelectedProduct((prev) => ({ ...prev, extras: newExtras }));
      handleApiSuccess(t("business.toasts.extraUpdated"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("business.toasts.extraUpdateFailedTitle"),
        defaultMessage: t("business.toasts.extraUpdateFailedMessage"),
      });
    }
  };

  const handleProductUpsellingUpdate = (upselling) => {
    if (selectedProduct) {
      setSelectedProduct((prev) => ({ ...prev, upselling }));
    }
  };

  const handleProductTaxUpdate = (tax) => {
    if (selectedProduct) {
      setSelectedProduct((prev) => ({
        ...prev,
        tax_id: tax ? tax.id : null,
        tax,
      }));
    }
  };

  const handleCreateProduct = () => {
    if (!selectedCategoryId) {
      toast.error(t("business.toasts.selectCategoryFirst"));
      return;
    }
    setProductToEdit(null);
    setIsCreateProductOpen(true);
  };

  const handleProductDeleted = (productId) => {
    // If the deleted product was selected, clear the selection and close extras modal
    if (selectedProduct?.id === productId) {
      setSelectedProduct(null);
      setIsExtrasModalOpen(false);
    }
  };

  const handleCreateExtra = () => {
    setExtraToEdit(null);
    setIsCreateExtraOpen(true);
  };

  const handleEditExtra = (extra) => {
    setExtraToEdit(extra);
    setIsCreateExtraOpen(true);
  };

  const handleDeleteExtra = (extra) => {
    setExtraToDelete(extra);
    setIsDeleteExtraOpen(true);
  };

  const confirmDeleteExtra = async () => {
    if (!extraToDelete) return;
    try {
      await deleteExtraMutation.mutateAsync({
        businessId: selectedBusiness.id,
        extraId: extraToDelete.id,
      });
      handleApiSuccess(t("business.toasts.extraDeleted"), toast);
      setIsDeleteExtraOpen(false);
      setExtraToDelete(null);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("business.toasts.extraDeleteFailedTitle"),
        defaultMessage: t("business.toasts.retryMessage"),
      });
    }
  };

  const handleCategoryDeleted = (categoryId) => {
    if (selectedCategoryId === categoryId) {
      setSelectedProduct(null);
      setProductPage(1);
      setSelectedCategoryId(null);
    }
  };

  const breadcrumbItems = [
    {
      label: t("business.breadcrumb.products"),
      path: "/menu/products",
      icon: "Package",
    },
    {
      label: selectedBusiness?.name || t("states.loading", { ns: "common" }),
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="flex h-[calc(100vh-140px)] flex-col space-y-4">
      <div className="flex-shrink-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {isLoadingBusiness ? (
        <div className="flex-shrink-0 rounded-lg border border-gray-800 bg-gray-900 p-4">
          <div className="flex min-w-0 animate-pulse items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-gray-800" />
            <div className="min-w-0 flex-1">
              <div className="mb-2 h-4 w-48 rounded bg-gray-800" />
              <div className="h-3 w-64 rounded bg-gray-800" />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-shrink-0 rounded-lg border border-gray-800 bg-gray-900 p-4">
          <div className="flex min-w-0 items-center gap-3">
            {selectedBusiness?.logo ? (
              <img
                src={selectedBusiness.logo}
                alt={selectedBusiness.name}
                className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gray-800">
                <Icon name="Store" className="h-6 w-6 text-gray-400" />
              </div>
            )}
            <div className="min-w-0">
              <div className="truncate font-medium text-white">
                {selectedBusiness?.name || ""}
              </div>
              <div className="truncate text-sm text-gray-400">
                {selectedBusiness?.address ||
                  selectedBusiness?.city?.name ||
                  ""}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POS Integration Banner */}
      {!isLoadingBusiness && hasPosIntegration && (
        <div className="flex-shrink-0">
          <POSIntegrationBanner />
        </div>
      )}

      {isLoadingBusiness ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 self-stretch lg:grid-cols-3">
          {/* Categories skeleton */}
          <div className="min-h-0 min-w-0 lg:col-span-1">
            <div className="animate-pulse rounded-lg border border-gray-800 bg-gray-900 p-4">
              <div className="mb-4 h-5 w-32 rounded bg-gray-800" />
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-9 rounded bg-gray-800" />
                ))}
              </div>
            </div>
          </div>
          {/* Products skeleton */}
          <div className="min-h-0 min-w-0 lg:col-span-2">
            <div className="animate-pulse rounded-lg border border-gray-800 bg-gray-900 p-4">
              <div className="mb-4 flex items-center justify-between">
                <div className="h-5 w-24 rounded bg-gray-800" />
                <div className="h-9 w-48 rounded bg-gray-800" />
              </div>
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-16 rounded bg-gray-800" />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 self-stretch lg:grid-cols-3 lg:[grid-template-rows:1fr]">
          <div className="min-h-0 min-w-0 lg:col-span-1">
            <CategoriesList
              categories={categories}
              hasPosIntegration={hasPosIntegration}
              businessId={selectedBusiness?.id}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={(id) => {
                setSelectedCategoryId(id);
                setSelectedProduct(null);
                setProductPage(1);
              }}
              onToggleCategory={handleCategoryToggle}
              selectedBusinessSlug={slug}
              onCategoryDeleted={handleCategoryDeleted}
              isLoadingToggle={toggleCategoryMutation.isPending}
            />
          </div>
          <div className="min-h-0 min-w-0 lg:col-span-2">
            <ProductsList
              products={displayedProducts}
              hasPosIntegration={hasPosIntegration}
              productsPagination={displayedProductsPagination}
              isLoadingProducts={isLoadingCurrentProducts}
              productPage={productPage}
              onPageChange={setProductPage}
              selectedProduct={selectedProduct}
              onSelectProduct={(product) => {
                setSelectedProduct(product);
                setIsExtrasModalOpen(true);
              }}
              onToggleProduct={handleProductToggle}
              isLoadingToggle={toggleProductMutation.isPending}
              productSearchInput={productSearchInput}
              onSearchChange={setProductSearchInput}
              onClearSearch={() => setProductSearchInput("")}
              selectedCategoryId={selectedCategoryId}
              showCategoryContext={isGlobalSearch}
              searchPlaceholder={t("business.search.placeholderAllCategories")}
              categoryLabelPrefix={t("business.search.categoryLabelPrefix")}
              uncategorizedLabel={t("business.search.uncategorized")}
              businessId={selectedBusiness?.id}
              onCreateProduct={handleCreateProduct}
            />
          </div>
        </div>
      )}

      <Modal
        isOpen={isExtrasModalOpen}
        onClose={() => {
          setIsExtrasModalOpen(false);
          setSelectedProduct(null);
          setActiveTab("extras"); // Reset to extras tab when closing
        }}
        title={t("business.modal.productTitle", {
          name: selectedProduct?.name || "",
        })}
        subtitle={
          hasPosIntegration
            ? t("business.modal.subtitlePos")
            : t("business.modal.subtitleDefault")
        }
        maxWidth="max-w-3xl"
      >
        {!hasPosIntegration ? (
          <>
            {/* Tabs Navigation - Only show if no POS integration */}
            <div className="mb-4 border-b border-gray-800">
              <nav className="flex space-x-4 overflow-x-auto sm:space-x-8">
                <button
                  onClick={() => setActiveTab("extras")}
                  className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                    activeTab === "extras"
                      ? "border-blue-500 text-white"
                      : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
                  }`}
                >
                  {t("business.tabs.extras")}
                </button>
                <button
                  onClick={() => setActiveTab("edit")}
                  className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                    activeTab === "edit"
                      ? "border-blue-500 text-white"
                      : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
                  }`}
                >
                  {t("business.tabs.editProduct")}
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === "extras" && (
                <ExtrasList
                  businessId={selectedBusiness?.id}
                  extras={extras}
                  hasPosIntegration={hasPosIntegration}
                  isLoadingExtras={isLoadingExtras}
                  selectedProduct={selectedProduct}
                  onToggleExtra={handleExtraToggle}
                  isLoadingToggle={updateExtrasMutation.isPending}
                  onCreateExtra={handleCreateExtra}
                  onEditExtra={handleEditExtra}
                  onDeleteExtra={handleDeleteExtra}
                  isDeletingExtra={deleteExtraMutation.isPending}
                />
              )}
              {activeTab === "edit" && (
                <ProductEditTab
                  businessId={selectedBusiness?.id}
                  categoryId={selectedProduct?.category_id}
                  selectedProduct={selectedProduct}
                  hasPosIntegration={hasPosIntegration}
                  onProductUpsellingUpdate={handleProductUpsellingUpdate}
                  onTaxUpdate={handleProductTaxUpdate}
                  onProductDeleted={handleProductDeleted}
                  onToggleProduct={handleProductToggle}
                  isLoadingToggle={toggleProductMutation.isPending}
                />
              )}
            </div>
          </>
        ) : (
          /* Show only Extras if POS integration is enabled */
          <ExtrasList
            businessId={selectedBusiness?.id}
            extras={extras}
            hasPosIntegration={hasPosIntegration}
            isLoadingExtras={isLoadingExtras}
            selectedProduct={selectedProduct}
            onToggleExtra={handleExtraToggle}
            isLoadingToggle={updateExtrasMutation.isPending}
            onCreateExtra={handleCreateExtra}
            onEditExtra={handleEditExtra}
            onDeleteExtra={handleDeleteExtra}
            isDeletingExtra={deleteExtraMutation.isPending}
          />
        )}
      </Modal>

      {/* Create/Edit Product Modal */}
      <CreateProductModal
        isOpen={isCreateProductOpen}
        onClose={() => {
          setIsCreateProductOpen(false);
          setProductToEdit(null);
        }}
        businessId={selectedBusiness?.id}
        categoryId={selectedCategoryId}
        productToEdit={productToEdit}
        onToggleProduct={handleProductToggle}
        onProductDeleted={handleProductDeleted}
        isLoadingToggle={toggleProductMutation.isPending}
        hasPosIntegration={hasPosIntegration}
      />

      {/* Create/Edit Extra Modal */}
      <CreateExtraModal
        isOpen={isCreateExtraOpen}
        onClose={() => {
          setIsCreateExtraOpen(false);
          setExtraToEdit(null);
        }}
        businessId={selectedBusiness?.id}
        extraToEdit={extraToEdit}
      />

      {/* Delete Extra Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteExtraOpen}
        onClose={() => {
          setIsDeleteExtraOpen(false);
          setExtraToDelete(null);
        }}
        onConfirm={confirmDeleteExtra}
        itemName={extraToDelete?.name}
        itemType={t("business.modal.extraItemType")}
        isLoading={deleteExtraMutation.isPending}
      />
    </div>
  );
};

export default ProductsBusinessPage;
